import { forwardRef, memo, useEffect, useImperativeHandle, useRef } from "react";
import { performanceStore } from "../performance/store";
import { AndroidWebRtcClient, type StreamProfile } from "./AndroidWebRtcClient";
import type { DirectTouch } from "./protobuf";
import { ScrcpyWebCodecsClient } from "./ScrcpyWebCodecsClient";
import { RemoteWaydroidClient } from "./RemoteWaydroidClient";
import { TeslaAndroidAudioClient, type TeslaAudioState } from "./TeslaAndroidAudioClient";
import { TeslaAndroidClient } from "./TeslaAndroidClient";
import { directControl } from "./controlStore";

export type StreamBackend = "native-webrtc" | "scrcpy-webcodecs" | "remote-waydroid" | "tesla-native";

type DirectClient = AndroidWebRtcClient | ScrcpyWebCodecsClient | RemoteWaydroidClient | TeslaAndroidClient;

export interface EmulatorHandle {
  reconnect: () => void;
  sendKey: (key: string) => void;
  rotate: () => void;
  setAudioEnabled: (enabled: boolean) => void;
}

interface Props {
  width: number;
  height: number;
  screenWidth: number;
  screenHeight: number;
  profile: StreamProfile;
  backend: StreamBackend;
  audioEnabled: boolean;
  fillViewport: boolean;
  onStateChange: (state: string) => void;
  onAudioStateChange: (state: TeslaAudioState) => void;
  onAudioError: (message: string) => void;
  onError: (message: string) => void;
}

const signalingUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/gateway/api/v1/emulator/ws-jsep`;

const scalePoint = (
  node: HTMLElement,
  clientX: number,
  clientY: number,
  screenWidth: number,
  screenHeight: number,
  clamp = false,
  fillViewport = false,
) => {
  const bounds = node.getBoundingClientRect();
  if (bounds.width <= 0 || bounds.height <= 0) return undefined;
  const renderedRatio = screenWidth / screenHeight;
  const boundsRatio = bounds.width / bounds.height;
  const renderedWidth = fillViewport
    ? bounds.width
    : boundsRatio > renderedRatio ? bounds.height * renderedRatio : bounds.width;
  const renderedHeight = fillViewport
    ? bounds.height
    : boundsRatio > renderedRatio ? bounds.height : bounds.width / renderedRatio;
  const offsetX = (bounds.width - renderedWidth) / 2;
  const offsetY = (bounds.height - renderedHeight) / 2;
  const relativeX = clientX - bounds.left - offsetX;
  const relativeY = clientY - bounds.top - offsetY;
  if (!clamp && (relativeX < 0 || relativeY < 0 || relativeX > renderedWidth || relativeY > renderedHeight)) return undefined;
  return {
    x: Math.max(0, Math.min(screenWidth - 1, Math.round(relativeX / renderedWidth * (screenWidth - 1)))),
    y: Math.max(0, Math.min(screenHeight - 1, Math.round(relativeY / renderedHeight * (screenHeight - 1)))),
  };
};

const EmulatorViewInner = forwardRef<EmulatorHandle, Props>(({
  width, height, screenWidth, screenHeight, profile, backend, audioEnabled, fillViewport,
  onStateChange, onAudioStateChange, onAudioError, onError,
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const callbacksRef = useRef({ onStateChange, onAudioStateChange, onAudioError, onError });
  const clientRef = useRef<DirectClient | undefined>(undefined);
  const audioClientRef = useRef<TeslaAndroidAudioClient | undefined>(undefined);
  const audioEnabledRef = useRef(audioEnabled);
  const activePointers = useRef(new Map<number, { pointerType: string; touch?: DirectTouch }>());

  useEffect(() => {
    callbacksRef.current = { onStateChange, onAudioStateChange, onAudioError, onError };
  }, [onStateChange, onAudioStateChange, onAudioError, onError]);

  useEffect(() => {
    window.__is4fEmulatorMountCount = (window.__is4fEmulatorMountCount || 0) + 1;
  }, []);

  const effectiveBackend: StreamBackend = backend === "scrcpy-webcodecs" && !ScrcpyWebCodecsClient.supported()
    ? "native-webrtc"
    : backend;
  const scrcpyUsesMse = effectiveBackend === "scrcpy-webcodecs" && !ScrcpyWebCodecsClient.webCodecsSupported();
  const remoteUsesMse = effectiveBackend === "remote-waydroid" && !RemoteWaydroidClient.webCodecsSupported();
  const teslaUsesMse = effectiveBackend === "tesla-native" && !TeslaAndroidClient.webCodecsSupported();

  useEffect(() => {
    const callbacks = {
      onState: (state: string) => callbacksRef.current.onStateChange(state),
      onError: (error: Error) => callbacksRef.current.onError(error.message),
    };
    const client: DirectClient = effectiveBackend === "tesla-native"
      ? new TeslaAndroidClient({
        ...callbacks,
        onFrame: (frame) => {
          const canvas = canvasRef.current;
          const context = canvas?.getContext("2d", { alpha: false, desynchronized: true });
          if (canvas && context) context.drawImage(frame, 0, 0, canvas.width, canvas.height);
          frame.close();
        },
      }, screenWidth, screenHeight, teslaUsesMse ? videoRef.current || undefined : undefined)
      : effectiveBackend === "remote-waydroid"
      ? new RemoteWaydroidClient({
        ...callbacks,
        onFrame: (frame) => {
          const canvas = canvasRef.current;
          const context = canvas?.getContext("2d", { alpha: false, desynchronized: true });
          if (canvas && context) context.drawImage(frame, 0, 0, canvas.width, canvas.height);
          frame.close();
        },
      }, screenWidth, screenHeight, remoteUsesMse ? videoRef.current || undefined : undefined)
      : effectiveBackend === "scrcpy-webcodecs"
      ? new ScrcpyWebCodecsClient({
        ...callbacks,
        onFrame: (frame) => {
          const canvas = canvasRef.current;
          const context = canvas?.getContext("2d", { alpha: false, desynchronized: true });
          if (canvas && context) context.drawImage(frame, 0, 0, canvas.width, canvas.height);
          frame.close();
        },
      }, profile, scrcpyUsesMse ? videoRef.current || undefined : undefined)
      : new AndroidWebRtcClient(signalingUrl, {
        ...callbacks,
        onTrack: (track) => {
          const video = videoRef.current;
          if (!video) return;
          const existing = video.srcObject instanceof MediaStream;
          const stream = existing ? video.srcObject as MediaStream : new MediaStream();
          if (!stream.getTracks().some((candidate) => candidate.id === track.id)) stream.addTrack(track);
          if (!existing) video.srcObject = stream;
          if (track.kind === "video") {
            void video.play().catch((error) => callbacksRef.current.onError(`Video playback failed: ${error}`));
          }
        },
      }, profile);
    clientRef.current = client;
    client.connect();
    if (effectiveBackend !== backend) {
      callbacksRef.current.onError("H.264 decoding is unavailable in this browser; using native WebRTC");
    }
    return () => {
      client.disconnect();
      if (clientRef.current === client) clientRef.current = undefined;
    };
  }, [effectiveBackend, remoteUsesMse, screenHeight, screenWidth, scrcpyUsesMse, teslaUsesMse]);

  useEffect(() => {
    const audio = audioRef.current;
    if (effectiveBackend !== "tesla-native" || !audio) return;
    const client = new TeslaAndroidAudioClient(audio, {
      onState: (state) => callbacksRef.current.onAudioStateChange(state),
      onError: (error) => callbacksRef.current.onAudioError(error.message),
    });
    audioClientRef.current = client;
    if (audioEnabledRef.current) client.setEnabled(true);
    return () => {
      client.disconnect();
      if (audioClientRef.current === client) audioClientRef.current = undefined;
    };
  }, [effectiveBackend]);

  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
    audioClientRef.current?.setEnabled(audioEnabled);
  }, [audioEnabled]);

  useEffect(() => { clientRef.current?.setProfile(profile); }, [profile]);

  useImperativeHandle(ref, () => ({
    reconnect: () => clientRef.current?.reconnect(),
    sendKey: (key) => clientRef.current?.sendKey(key, 2),
    rotate: () => clientRef.current?.rotate(),
    setAudioEnabled: (enabled) => {
      audioEnabledRef.current = enabled;
      audioClientRef.current?.setEnabled(enabled);
    },
  }), []);

  const touchFromEvent = (event: React.PointerEvent<HTMLDivElement>, down: boolean): DirectTouch | undefined => {
    const point = scalePoint(event.currentTarget, event.clientX, event.clientY, screenWidth, screenHeight, true, fillViewport);
    if (!point) return undefined;
    return {
      ...point,
      identifier: event.pointerId,
      pressure: down ? Math.max(1, Math.round((event.pressure || 0.5) * 0x7fff)) : 0,
      touchMajor: Math.max(1, Math.round(event.width || 1)),
      touchMinor: Math.max(1, Math.round(event.height || 1)),
    };
  };

  const sendPointer = (event: React.PointerEvent<HTMLDivElement>, down: boolean, essential: boolean) => {
    if (!directControl.canHumanControl()) return;
    const point = scalePoint(event.currentTarget, event.clientX, event.clientY, screenWidth, screenHeight, !down, fillViewport);
    if (!point) return;
    // Treat primary mouse interaction as a touchscreen contact. Android games
    // commonly ignore SOURCE_MOUSE even though the event reached the device.
    // The RTC touch protocol is still direct and supports the same immediate
    // down/up delivery plus multiple browser touch contacts.
    const touch = touchFromEvent(event, down);
    if (!touch) return;
    activePointers.current.set(event.pointerId, { pointerType: event.pointerType, touch });
    clientRef.current?.sendTouch(
      [...activePointers.current.values()].flatMap((pointer) => pointer.touch ? [pointer.touch] : []),
      essential,
    );
  };

  const pointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!directControl.canHumanControl()) return;
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    activePointers.current.set(event.pointerId, { pointerType: event.pointerType });
    directControl.setHumanGesture(true);
    sendPointer(event, true, true);
  };

  const pointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!activePointers.current.has(event.pointerId)) return;
    event.preventDefault();
    directControl.setHumanGesture(true);
    const native = event.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
    const samples = native.getCoalescedEvents?.() || [native];
    const latest = samples[samples.length - 1];
    const point = scalePoint(event.currentTarget, latest.clientX, latest.clientY, screenWidth, screenHeight, false, fillViewport);
    if (!point) return;
    const touch = {
      ...point,
      identifier: event.pointerId,
      pressure: Math.max(1, Math.round((latest.pressure || 0.5) * 0x7fff)),
      touchMajor: Math.max(1, Math.round(latest.width || 1)),
      touchMinor: Math.max(1, Math.round(latest.height || 1)),
    };
    activePointers.current.set(event.pointerId, { pointerType: event.pointerType, touch });
    clientRef.current?.sendTouch(
      [...activePointers.current.values()].flatMap((pointer) => pointer.touch ? [pointer.touch] : []),
    );
  };

  const pointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!activePointers.current.has(event.pointerId)) return;
    event.preventDefault();
    sendPointer(event, false, true);
    activePointers.current.delete(event.pointerId);
    if (activePointers.current.size === 0) directControl.setHumanGesture(false);
  };

  const releaseAllPointers = () => {
    if (activePointers.current.size > 0) {
      clientRef.current?.sendTouch([], true);
      activePointers.current.clear();
    }
    directControl.setHumanGesture(false);
  };

  const keyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!directControl.canHumanControl() || event.repeat) return;
    event.preventDefault();
    clientRef.current?.sendKey(event.key, 0);
  };

  const keyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!directControl.canHumanControl()) return;
    event.preventDefault();
    clientRef.current?.sendKey(event.key, 1);
  };

  const wheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!directControl.canHumanControl()) return;
    event.preventDefault();
    const point = scalePoint(event.currentTarget, event.clientX, event.clientY, screenWidth, screenHeight, true, fillViewport);
    if (point) clientRef.current?.sendScroll(point.x, point.y, event.deltaX, event.deltaY);
  };

  useEffect(() => {
    const release = () => releaseAllPointers();
    const releaseWhenHidden = () => {
      if (document.visibilityState !== "visible") releaseAllPointers();
    };
    window.addEventListener("blur", release);
    window.addEventListener("pagehide", release);
    document.addEventListener("visibilitychange", releaseWhenHidden);
    return () => {
      window.removeEventListener("blur", release);
      window.removeEventListener("pagehide", release);
      document.removeEventListener("visibilitychange", releaseWhenHidden);
      releaseAllPointers();
    };
  }, []);

  useEffect(() => {
    let frame: number;
    const held = new Set<number>();
    const keys = ["GoBack", "GoHome", "AppSwitch", "AudioVolumeDown", "AudioVolumeUp"];
    const poll = () => {
      if (directControl.canHumanControl()) {
        for (const gamepad of navigator.getGamepads?.() || []) {
          if (!gamepad) continue;
          [1, 12, 9, 6, 7].forEach((buttonIndex, mappingIndex) => {
            const id = gamepad.index * 100 + buttonIndex;
            if (gamepad.buttons[buttonIndex]?.pressed && !held.has(id)) {
              held.add(id);
              clientRef.current?.sendKey(keys[mappingIndex], 2);
            } else if (!gamepad.buttons[buttonIndex]?.pressed) {
              held.delete(id);
            }
          });
        }
      }
      frame = requestAnimationFrame(poll);
    };
    frame = requestAnimationFrame(poll);
    return () => {
      cancelAnimationFrame(frame);
      directControl.setHumanGesture(false);
    };
  }, []);

  return <div
    ref={inputRef}
    className="direct-input-surface"
    style={{ width, height }}
    tabIndex={0}
    onPointerDown={pointerDown}
    onPointerMove={pointerMove}
    onPointerUp={pointerUp}
    onPointerCancel={(event) => { event.preventDefault(); releaseAllPointers(); }}
    onLostPointerCapture={releaseAllPointers}
    onKeyDown={keyDown}
    onKeyUp={keyUp}
    onWheel={wheel}
    onContextMenu={(event) => event.preventDefault()}
  >
    {effectiveBackend === "tesla-native" && <audio ref={audioRef} className="tesla-audio" playsInline preload="none" aria-hidden="true" />}
    {(effectiveBackend === "native-webrtc" || scrcpyUsesMse || remoteUsesMse || teslaUsesMse) ? <video
      ref={videoRef}
      className="live-screen"
      width={width}
      height={height}
      autoPlay
      muted={scrcpyUsesMse || remoteUsesMse || teslaUsesMse || profile !== "quality"}
      playsInline
      disablePictureInPicture
      onPlaying={() => { performanceStore.update({ connectionState: "connected" }); callbacksRef.current.onStateChange("streaming"); }}
    /> : <canvas
      ref={canvasRef}
      className="live-screen"
      width={screenWidth}
      height={screenHeight}
      style={{ width, height }}
    />}
  </div>;
});

declare global {
  interface Window { __is4fEmulatorMountCount?: number; }
}

EmulatorViewInner.displayName = "EmulatorView";

export const EmulatorView = memo(EmulatorViewInner);
